<template>
	<v-row class="block-editor-widget-settings-text-input field-settings" no-gutters>
		<v-col cols="12" class="text-left py-3" v-if="localField">
			<div class="field-setting-section">
				<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId">
					<template #default="{ indicator, isUpgradeRequired }">
						<div class="flex-space-between--center mr-3">
							<div v-if="hasEditPermissions || dropdownOptions.length > 0" class="settings-title">URL parameter options</div>
							<div class="pl-2 pointer" v-if="isUpgradeRequired && isCustomerFeature">
								<component :is="indicator" :billingFeatureId="billingFeatureId" iconOnly canBuild />
							</div>
						</div>
					</template>
				</MaybeUpgradeRequiredIndicator>
				<perfect-scrollbar class="dropdown-options-section">
					<div v-for="opt in dropdownOptions" :key="opt.id">
						<ParamOption
							class="option-container mr-3"
							:ref="`dropdownOption-${opt.id}`"
							:data="opt"
							:is-option-value="false"
							@updateOption="updateOption"
							@onEnterKey="addOption"
							@removeOption="removeOption"
							:can-remove="true"
							:readonly="!hasEditPermissions"
						/>
					</div>
				</perfect-scrollbar>
				<div v-if="hasEditPermissions" class="add-option inline-link-style highlighted" @click="addOption">[+] Add Parameter</div>
			</div>
		</v-col>
	</v-row>
</template>

<script>
import { isEqual } from 'lodash';
import { mapActions } from 'vuex';

import ParamOption from './ParamOption';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import { setParamInEditNode } from '@/store/modules/selectedNode/actions';
import { editNode } from '@/store/modules/selectedNode/getters';
import { canEditWorkflow, getWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';

const getOption = (options, optionObj) => {
	const maxId = options.length === 0 ? 0 : Math.max(...options.map((o) => o.id));
	return { id: maxId + 1, ...optionObj };
};

export default {
	name: 'ParamOptionSettings',
	components: {
		ParamOption,
		MaybeUpgradeRequiredIndicator,
	},
	computed: {
		...mapGettersShorthand({
			getWorkflow,
			editNode,
			canEditWorkflow,
		}),
		billingFeatureId() {
			return FeatureTypes.CustomerUrlParameters;
		},
		dropdownOptions: {
			get() {
				return this.localField.dropdownOptions || [];
			},
			set(value) {
				this.$set(this.localField, 'dropdownOptions', value);
			},
		},
		urlParameters() {
			return this.editNode.params_map?.url_parameters;
		},
		isCustomerFeature() {
			return WorkflowService.isCustomerFacingFeature(this.getWorkflow(this.editNode?.workflow));
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.editNode?.workflow);
		},
	},
	created() {
		this.setLocalField();
	},
	methods: {
		...mapActions([setParamInEditNode.fullPath]),
		setLocalField() {
			const paramsMap = this.urlParameters;
			const dropdownOptions = [];
			if (paramsMap) {
				Object.entries(paramsMap).forEach(([key, value]) => {
					dropdownOptions.push(getOption(dropdownOptions, { text: key, value }));
				});
			}

			this.localField = { dropdownOptions };
		},
		addOption() {
			const newOption = getOption(this.dropdownOptions, { text: '', value: '' });
			this.dropdownOptions.push(newOption);
			this.$nextTick(() => {
				this.$refs[`dropdownOption-${newOption.id}`][0].focusInput();
			});
		},
		updateOption(optionData) {
			const optIndex = this.dropdownOptions.findIndex((o) => o.id === optionData.id);
			this.$set(this.dropdownOptions, optIndex, optionData);
		},
		removeOption(opt) {
			this.dropdownOptions = this.dropdownOptions.filter((o) => o.id !== opt.id);
		},
	},
	watch: {
		localField: {
			handler() {
				const nonEmptyOptions = this.localField.dropdownOptions.filter((x) => x.text !== '');

				this.$emit('updateOptions', nonEmptyOptions);

				const updatedUurlParameters = nonEmptyOptions.reduce(
					(updatedUurlParameters, x) => ({ ...updatedUurlParameters, ...{ [x.text]: x.value } }),
					{}
				);
				if (!isEqual(updatedUurlParameters, this.urlParameters)) {
					this[setParamInEditNode.fullPath]({ key: 'params_map', value: { url_parameters: updatedUurlParameters } });
				}
			},
			deep: true,
		},
	},
	data: () => ({
		localField: {},
	}),
};
</script>

<style scoped lang="scss">
.block-editor-widget-settings-text-input {
	.option-container {
		margin: 3px 0 3px 0;
	}
	.add-option {
		margin: 3px 0 0 0;
		font-size: 14px;
	}
	.field-setting-section {
		.dropdown-options-section {
			max-height: 178px;
		}
	}

	.v-select-container:not(.is-selectable) {
		pointer-events: none;
		opacity: 0.6;
		transition: 0.3;
	}
}
</style>
